import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import axios from "../../util/axiosInstance";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

type ReportRow = {
  unitId: string;
  name: string;
  year?: number;
  month?: number;
  down?: number;
  up?: number;
  customer?: number;
  precision?: number;
  shutDowns?: number;
  // allow any extra fields returned by the API
  [key: string]: any;
};

type Props = {
  open: boolean;
  onClose: () => void;
  groupId?: string;
  groupName?: string;
  title?: string;
  mOffset?: number; // number of months for the report
};

export default function LastSixMonthReportDialog({
  open,
  onClose,
  groupId,
  groupName,
  title = "Report",
  mOffset = 6,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<ReportRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showExportMenu, setShowExportMenu] = useState(false);

  const fetchReportData = async () => {
    if (!groupId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/devices?groupId=${groupId}`
      );
      const unitsData = response.data || [];
      const unitIds = unitsData.map((u: any) => u.id);
      const nameMap: Record<string, string> = unitsData.reduce(
        (acc: any, u: any) => {
          acc[u.id] = u.name;
          return acc;
        },
        {}
      );

      if (unitIds.length === 0) {
        setReportData([]);
        setError("No units found for this group.");
        return;
      }

      const payload = {
        mOffset,
        units: unitIds,
      };

      const reportResponse = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/reports/precision-report-n-months`,
        payload
      );

      // API returns list under .myList (per original). Fallback to [].
      const data = reportResponse.data?.myList || [];
      const parsed: ReportRow[] = data.map((item: any) => ({
        ...item,
        name: nameMap[item.unitId] || item.unitId,
      }));

      setReportData(parsed);
    } catch (err) {
      console.error(`Error fetching ${title}:`, err);
      setError("Failed to load report data.");
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && groupId) fetchReportData();
    // reset search when opening
    if (open) setSearchQuery("");
  }, [open, groupId, mOffset]);

  const filteredData = reportData.filter((row) =>
    (row.name || "")
      .toString()
      .toLowerCase()
      .includes(searchQuery.trim().toLowerCase())
  );

  const copyToClipboard = async () => {
    if (!filteredData || filteredData.length === 0) {
      toast.warning("No report data available to copy");
      return;
    }

    const headers =
      "Unit Name\tYear\tMonth\tStop(Hrs)\tRun(Hrs)\tCustomer(Hrs)\tPRC(Hrs)\tShutdowns";
    const rows = filteredData
      .map((item) => {
        const year = item.year ?? "";
        const month = item.month ?? "";
        const down = item.down ?? "";
        const up = item.up ?? "";
        const customer = item.customer ?? "";
        const precision = item.precision ?? "";
        const shutDowns = item.shutDowns ?? "";
        return `${item.name}\t${year}\t${month}\t${down}\t${up}\t${customer}\t${precision}\t${shutDowns}`;
      })
      .join("\n");

    try {
      await navigator.clipboard.writeText(`${headers}\n${rows}`);
      toast.success("Report copied to clipboard");
    } catch (err) {
      console.error("Failed to copy:", err);
      toast.error("Failed to copy report to clipboard");
    }
  };

  const exportToCSV = () => {
    if (!filteredData || filteredData.length === 0) {
      toast.warning("No report data available to export");
      return;
    }

    const csvContent = [
      [
        "Unit Name",
        "Year",
        "Month",
        "Stop (Hrs)",
        "Run (Hrs)",
        "Customer (Hrs)",
        "PRC (Hrs)",
        "Shutdowns",
      ],
      ...filteredData.map((item) => [
        item.name,
        item.year ?? "",
        item.month ?? "",
        item.down ?? "",
        item.up ?? "",
        item.customer ?? "",
        item.precision ?? "",
        item.shutDowns ?? "",
      ]),
    ];

    const csvString = csvContent
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    const filenameSafe = (groupName || "report").replace(/\s+/g, "_");
    link.download = `${title.replace(/\s+/g, "_")}_${filenameSafe}.csv`;
    link.click();
    toast.success("Exported CSV");
  };

  const exportToExcel = () => {
    if (!filteredData || filteredData.length === 0) {
      toast.warning("No report data available to export");
      return;
    }

    const headers = [
      "Unit Name",
      "Year",
      "Month",
      "Stop (Hrs)",
      "Run (Hrs)",
      "Customer (Hrs)",
      "PRC (Hrs)",
      "Shutdowns",
    ];
    const rows = filteredData.map((item) => [
      item.name,
      item.year ?? "",
      item.month ?? "",
      item.down ?? "",
      item.up ?? "",
      item.customer ?? "",
      item.precision ?? "",
      item.shutDowns ?? "",
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
    const filenameSafe = (groupName || "report").replace(/\s+/g, "_");
    XLSX.writeFile(
      workbook,
      `${title.replace(/\s+/g, "_")}_${filenameSafe}.xlsx`
    );
    toast.success("Exported Excel");
  };

  const exportToPDF = () => {
    if (!filteredData || filteredData.length === 0) {
      toast.warning("No report data available to export");
      return;
    }

    const doc = new jsPDF("l", "pt", "a4"); // landscape
    doc.setFontSize(12);
    doc.text(`${title} ${groupName ? `- ${groupName}` : ""}`, 40, 30);

    const head = [
      [
        "Unit Name",
        "Year",
        "Month",
        "Stop (Hrs)",
        "Run (Hrs)",
        "Customer (Hrs)",
        "PRC (Hrs)",
        "Shutdowns",
      ],
    ];

    const body = filteredData.map((item) => [
      item.name,
      item.year ?? "",
      // If month number present (1-12 or 0-11), attempt to format, else show raw
      typeof item.month === "number" && item.month >= 0
        ? new Date(
            item.year ?? new Date().getFullYear(),
            (item.month || 1) - 1
          ).toLocaleString("default", { month: "long" })
        : item.month ?? "",
      item.down ?? "",
      item.up ?? "",
      item.customer ?? "",
      item.precision ?? "",
      item.shutDowns ?? "",
    ]);

    autoTable(doc, {
      head,
      body,
      startY: 50,
      theme: "grid",
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [41, 128, 185] },
      tableWidth: "auto",
    });

    const filenameSafe = (groupName || "report").replace(/\s+/g, "_");
    doc.save(`${title.replace(/\s+/g, "_")}_${filenameSafe}.pdf`);
    toast.success("Exported PDF");
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded bg-slate-800 p-6 text-left align-middle shadow-xl transition-all">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">
                  {title} {groupName ? `- ${groupName}` : ""}
                </h3>
                <button
                  onClick={onClose}
                  aria-label="Close dialog"
                  className="text-gray-400 hover:text-white"
                >
                  {/* simple X icon */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 011.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>

              {/* Search + Buttons */}
              <div className="flex items-center justify-between mb-4">
                {/* Search (left) */}
                <div className="flex items-center">
                  <label htmlFor="report-search" className="sr-only">
                    Search unit
                  </label>
                  <div className="relative">
                    <input
                      id="report-search"
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search unit..."
                      className="w-64 rounded bg-slate-700 border border-gray-600 px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="absolute inset-y-0 left-0 flex items-center pl-2 pointer-events-none">
                      {/* small search icon svg */}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Buttons (right) */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={copyToClipboard}
                    className="px-3 py-2 rounded bg-gray-600 text-white hover:bg-gray-700"
                  >
                    Copy
                  </button>
                  <button
                    onClick={exportToCSV}
                    className="px-3 py-2 rounded bg-gray-600 text-white hover:bg-gray-700"
                  >
                    CSV
                  </button>
                  <button
                    onClick={exportToExcel}
                    className="px-3 py-2 rounded bg-gray-600 text-white hover:bg-gray-700"
                  >
                    Excel
                  </button>
                  <button
                    onClick={exportToPDF}
                    className="px-3 py-2 rounded bg-gray-600 text-white hover:bg-gray-700"
                  >
                    PDF
                  </button>
                </div>
              </div>

              {/* Content */}
              {loading ? (
                <div className="flex justify-center py-10">
                  <div className="h-12 w-12 animate-spin rounded-full border-4 border-t-4 border-t-blue-500 border-gray-700" />
                </div>
              ) : error ? (
                <div className="text-center text-red-400 py-4">{error}</div>
              ) : filteredData.length === 0 ? (
                <div className="text-center text-gray-300 py-4">
                  No report data available
                </div>
              ) : (
                <div className="overflow-x-auto max-h-96 overflow-y-auto border border-gray-700 rounded">
                  <table className="w-full text-sm text-left text-white">
                    <thead className="bg-slate-700 text-gray-200 sticky top-0">
                      <tr>
                        {[
                          "Unit Name",
                          "Year",
                          "Month",
                          "Stop(Hrs)",
                          "Run(Hrs)",
                          "Customer(Hrs)",
                          "PRC(Hrs)",
                          "Shutdowns",
                        ].map((h) => (
                          <th
                            key={h}
                            className="px-3 py-2 border-b border-gray-600"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      {filteredData.map((row, idx) => (
                        <tr
                          key={`${row.unitId}-${idx}`}
                          className={
                            idx % 2 === 0 ? "bg-slate-800" : "bg-slate-700"
                          }
                        >
                          <td className="px-3 py-2">{row.name}</td>
                          <td className="px-3 py-2">{row.year ?? ""}</td>
                          <td className="px-3 py-2">
                            {typeof row.month === "number" && row.month >= 0
                              ? new Date(
                                  row.year ?? new Date().getFullYear(),
                                  (row.month || 1) - 1
                                ).toLocaleString("default", {
                                  month: "long",
                                })
                              : row.month ?? ""}
                          </td>
                          <td className="px-3 py-2">
                            {row.down?.toFixed(1) ?? ""}
                          </td>
                          <td className="px-3 py-2">
                            {row.up?.toFixed(1) ?? ""}
                          </td>
                          <td className="px-3 py-2">
                            {row.customer?.toFixed(1) ?? ""}
                          </td>
                          <td className="px-3 py-2">
                            {row.precision?.toFixed(1) ?? ""}
                          </td>
                          <td className="px-3 py-2">{row.shutDowns ?? ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Dialog.Panel>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
